import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader2, Send } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const RSVPForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    attendance_days: 'both',
    number_of_guests: 1,
    plus_one_allowed: false,
    plus_one_name: '',
    meal_preference: '',
    dietary_restrictions: '',
    custom_field_responses: {},
  });
  const [rsvpSettings, setRsvpSettings] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(API_ENDPOINTS.GET_RSVP_FORM);
        if (!cancelled && res.data.success) {
          setRsvpSettings(res.data.settings);
          setFields(res.data.fields || []);
        }
      } catch {
        if (!cancelled) setError('Could not load RSVP form. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCustomField = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      custom_field_responses: { ...prev.custom_field_responses, [fieldId]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(API_ENDPOINTS.SUBMIT_RSVP, formData);
      if (res.data.success) {
        setSubmitted(true);
        onSuccess?.();
      } else {
        setError(res.data.message || 'Could not submit RSVP.');
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError('Something went wrong. Please check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rsvp-loading">
        <Loader2 size={28} className="spin" />
        <p>Loading your RSVP form…</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="rsvp-success">
        <CheckCircle size={56} />
        <h3>You're on the list!</h3>
        <p>Thank you for confirming. We can't wait to celebrate with you.</p>
        {formData.email && (
          <p className="rsvp-success-note">A confirmation may be sent to {formData.email}.</p>
        )}
      </div>
    );
  }

  if (rsvpSettings && String(rsvpSettings.is_open) === '0') {
    return (
      <div className="rsvp-closed">
        <AlertCircle size={48} />
        <h3>RSVP is closed</h3>
        <p>Please contact the couple directly if you still need to respond.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rsvp-form">
      <p className="rsvp-intro">
        Your response helps us plan seating, meals, and a smooth celebration for everyone.
      </p>



      {fields.map((field) => (
        <div key={field.id} className={`form-group ${field.field_type === 'checkbox' ? 'form-group-checkbox' : ''}`}>
          {field.field_type !== 'checkbox' && (
            <label>
              {field.field_label}
              {field.is_required ? ' *' : ''}
            </label>
          )}
          
          {field.help_text && (
            <span className="field-help-text" style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '0.5rem' }}>
              {field.help_text}
            </span>
          )}

          {(field.field_type === 'text' || field.field_type === 'email' || field.field_type === 'phone' || field.field_type === 'number') && (
            <input
              type={field.field_type === 'phone' ? 'tel' : field.field_type}
              value={formData.custom_field_responses[field.id] || ''}
              onChange={(e) => handleCustomField(field.id, e.target.value)}
              placeholder={field.placeholder_text || ''}
              required={field.is_required}
            />
          )}

          {field.field_type === 'textarea' && (
            <textarea
              rows="3"
              value={formData.custom_field_responses[field.id] || ''}
              onChange={(e) => handleCustomField(field.id, e.target.value)}
              placeholder={field.placeholder_text || ''}
              required={field.is_required}
            />
          )}

          {field.field_type === 'select' && (
            <select
              value={formData.custom_field_responses[field.id] || ''}
              onChange={(e) => handleCustomField(field.id, e.target.value)}
              required={field.is_required}
            >
              <option value="">Select…</option>
              {field.options &&
                (Array.isArray(field.options) ? field.options : JSON.parse(field.options)).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
            </select>
          )}

          {field.field_type === 'radio' && field.options && (
            <div className="radio-group" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {(Array.isArray(field.options) ? field.options : JSON.parse(field.options)).map((opt) => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name={`field_${field.id}`}
                    value={opt}
                    checked={formData.custom_field_responses[field.id] === opt}
                    onChange={(e) => handleCustomField(field.id, e.target.value)}
                    required={field.is_required}
                  />
                  {opt}
                </label>
              ))}
            </div>
          )}

          {field.field_type === 'checkbox' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={!!formData.custom_field_responses[field.id]}
                onChange={(e) => handleCustomField(field.id, e.target.checked)}
                required={field.is_required}
              />
              {field.field_label}
              {field.is_required ? ' *' : ''}
            </label>
          )}
        </div>
      ))}

      {error && <div className="error-message">{error}</div>}

      <button type="submit" className="btn-primary btn-block" disabled={submitting}>
        {submitting ? (
          <>
            <Loader2 size={18} className="spin" /> Sending…
          </>
        ) : (
          <>
            <Send size={18} /> Submit RSVP
          </>
        )}
      </button>
    </form>
  );
};

export default RSVPForm;
